import type { HasParentDefinition } from 'contracts/tess';

// eslint-disable-next-line no-restricted-imports
import { getOrCreateThreeColor } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import { ENTITY_TYPES } from '../../constants';
import type { STEPBody } from './STEPBody';
import type { RefMetaData } from './STEPGeometry';
import { StepEdgeModel } from './StepEdgeModel';
import { StepFaceModel } from './StepFaceModel';
import type { StepTextModel } from './StepTextModel';
import { constructEntityName } from './utils';

// import type { StepVertexBody } from './StepVertexBody';
// import { constructEntityName } from './utils';

// This is the object stored on the StepEdgeModel (spread into 'meta') for handling visibility + layer assignment in vectorization-utils
// Style.layer === Layer.key
// Style.type is the DXF entity name, used for grouping in the vectorEditor display
// Style.visible is read by paint-utils to set the visibility/opacity on the StepEdgeModel level
export type Style = HasParentDefinition & {
  visible: 0 | 1 | 2 | boolean;
  layer: string;
  color: number;
  type: string;
  isRef?: boolean;
};

export type StyleAdapter = StepStyleAdapter | RefStyleAdapter;

export class StepStyleAdapter implements Style {
  public visible = true;
  public rootRef?: RefMetaData;

  constructor(
    public readonly model: StepEdgeModel | StepTextModel | StepFaceModel,
    public readonly body: STEPBody,
  ) {
    model.host.vectorStyleByNameMap.set(model.name, this);

    if (this.model.metaData.parentIndex) {
      this.rootRef = this.body.getRef(this.model.metaData.parentIndex);
      if (this.rootRef) {
        //Walk up tree of refs until the root is found
        while (this.rootRef.parentIndex) {
          const candidate = this.body.getEntityMeta(
            this.rootRef.parentIndex,
            ENTITY_TYPES.REF,
          ) as RefMetaData | undefined;
          if (candidate) {
            this.rootRef = candidate;
          } else {
            break;
          }
        }
      }
    }
  }

  public get layer(): string {
    return this.model.metaData.layer ?? '';
  }
  public set layer(v: string) {
    if (this.rootRef) {
      for (const ref of this.body.partGeometry.getSelfAndDecendentRefs(
        this.rootRef,
      )) {
        ref.layer = v;
      }
      const vectorStyleByNameMap = this.body.vectorStyleByNameMap;
      for (const [type, { index }] of this.body.partGeometry.getEntitiesForRef(
        this.rootRef,
      )) {
        // Skip vertex in visible calc (not shown in layers panel)
        if (type === ENTITY_TYPES.VERTEX) {
          continue;
        }
        const name = constructEntityName(
          this.body.id ?? 'undefined',
          //Note: This is not to be confused with this.type which corresponds to the DXF entity type.
          //Here `type` is the MetaDataType ('face', 'edge', 'vertex', 'text')
          type,
          index,
        );

        // Update the layer value directly on the model, rather than calling the setter here
        const _this = vectorStyleByNameMap.get(name);
        if (_this) {
          if (_this instanceof StepStyleAdapter) {
            _this.model.metaData.layer = v;
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
          } else if (_this instanceof RefStyleAdapter) {
            _this.ref.layer = v;
          }
        }
      }
    } else {
      this.model.metaData.layer = v;
    }
  }
  public get color(): number {
    return getOrCreateThreeColor(this.model.color).getHex();
  }
  public get type(): string {
    return this.model.metaData.type ?? '';
  }
  public get parentIndex(): number | undefined {
    return this.model.metaData.parentIndex;
  }
  public *[Symbol.iterator](): IterableIterator<
    [keyof Style, Style[keyof Style]]
  > {
    for (const prop in this) {
      // filter out args passed to constructor
      if (prop !== 'model') {
        yield [prop, this[prop]] as [keyof Style, Style[keyof Style]];
      }
    }
  }
  public toJson(): Style {
    return Object.fromEntries(this[Symbol.iterator]()) as Style;
  }
}

const enumerable = { enumerable: true };

// getters/setters are not enumerable, so made enumerable via Object.defineProperty for use in deep copying in vectorEditor/data/reducer.ts
// defined on prototype to for..in loop must be used when consuming (for..in supports inherited enumerable properties)
Object.defineProperties(StepStyleAdapter.prototype, {
  layer: enumerable,
  color: enumerable,
  type: enumerable,
});

//RefStyleAdapter is for `ref` entities, and is used to control visibility and layer of the many entities under the `def` it references.
export class RefStyleAdapter implements Style {
  public readonly isRef = true;
  public readonly rootRef: RefMetaData;

  constructor(
    public readonly ref: RefMetaData,
    public readonly body: STEPBody,
  ) {
    body.vectorStyleByNameMap.set(`${ref.index}`, this);
    this.rootRef = this.ref;
    //Walk up tree of refs until the root is found
    while (this.rootRef.parentIndex) {
      const candidate = this.body.getEntityMeta(
        this.rootRef.parentIndex,
        ENTITY_TYPES.REF,
      ) as RefMetaData | undefined;
      if (candidate) {
        this.rootRef = candidate;
      } else {
        break;
      }
    }
  }

  // @return 0: none visible, 1: some visible, 2: all visible (some visible && none hidden), Truthy == 1 | 2: atleast some are visible, Falsey == 0: none visible
  public get visible(): 0 | 1 | 2 | boolean {
    let someVisible = false;
    let someHidden = false;

    const vectorStyleByNameMap = this.body.vectorStyleByNameMap;
    for (const [type, entity] of this.body.partGeometry.getEntitiesForRef(
      this.rootRef,
    )) {
      // Skip vertex in visible calc (not shown in layers panel)
      if (type === ENTITY_TYPES.VERTEX) {
        continue;
      }

      const name = constructEntityName(
        this.body.id ?? 'undefined',
        type,
        entity.index,
      );

      const style = vectorStyleByNameMap.get(name);
      if (!style) {
        continue;
      }

      if (style.visible) {
        someVisible = true;
        if (someHidden) {
          break;
        }
      } else {
        someHidden = true;
        if (someVisible) {
          break;
        }
      }
    }

    // if (!(someVisible ? (someHidden ? 1 : 2) : 0)) debugger;
    //0: none visible
    //1: some visible
    //2: all visible (some visible && none hidden)
    //Truthy == 1 | 2: atleast some are visible
    //Falsey == 0: none visible
    return (someVisible ? (someHidden ? 1 : 2) : 0) as 0 | 1 | 2 | boolean;
  }

  // @return 0: all will be set to not visible, 1 | 2: all will be changed to visible
  public set visible(v: boolean | 0 | 1 | 2) {
    const vectorStyleByNameMap = this.body.vectorStyleByNameMap;
    for (const [type, { index }] of this.body.partGeometry.getEntitiesForRef(
      this.rootRef,
    )) {
      // Skip vertex in visible calc (not shown in layers panel)
      if (type === ENTITY_TYPES.VERTEX) {
        continue;
      }
      const name = constructEntityName(
        this.body.id ?? 'undefined',
        //Note: This is not to be confused with this.type which corresponds to the DXF entity type.
        //Here `type` is the MetaDataType ('face', 'edge', 'vertex', 'text')
        type,
        index,
      );
      const style = vectorStyleByNameMap.get(name);
      if (style) {
        style.visible = !!v;
      }
    }
  }

  public get color(): number {
    return this.body.color.getHex();
  }

  public get layer(): string {
    return this.ref.layer ?? '';
  }
  // Not currently used
  public set layer(v: string) {
    for (const ref of this.body.partGeometry.getSelfAndDecendentRefs(
      this.rootRef,
    )) {
      ref.layer = v;
    }
    //must also update its self's descendents, and siblings and their descendents
    const vectorStyleByNameMap = this.body.vectorStyleByNameMap;
    for (const [type, { index }] of this.body.partGeometry.getEntitiesForRef(
      this.rootRef,
    )) {
      // Skip vertex in visible calc (not shown in layers panel)
      if (type === ENTITY_TYPES.VERTEX) {
        continue;
      }
      const name = constructEntityName(
        this.body.id ?? 'undefined',
        //Note: This is not to be confused with this.type which corresponds to the DXF entity type.
        //Here `type` is the MetaDataType ('face', 'edge', 'vertex', 'text')
        type,
        index,
      );
      // Update the layer value directly on the model, rather than calling the setter here
      const _this = vectorStyleByNameMap.get(name);
      if (_this) {
        if (_this instanceof StepStyleAdapter) {
          _this.model.metaData.layer = v;
        } else if (_this instanceof RefStyleAdapter) {
          _this.ref.layer = v;
        }
      }
    }
  }
  public get type(): string {
    return this.ref.type ?? 'ref';
  }
  public *[Symbol.iterator](): IterableIterator<
    [keyof Style, Style[keyof Style]]
  > {
    for (const prop in this) {
      // filter out args passed to constructor
      if (prop !== 'body' && prop !== 'ref') {
        yield [prop, this[prop]] as [keyof Style, Style[keyof Style]];
      }
    }
  }
  public toJson(): Style {
    return Object.fromEntries(this[Symbol.iterator]()) as Style;
  }
}

Object.defineProperties(RefStyleAdapter.prototype, {
  visible: enumerable,
  layer: enumerable,
  color: enumerable,
  type: enumerable,
});

export const getModelType = (styleAdapter: StyleAdapter) => {
  if (styleAdapter instanceof RefStyleAdapter) {
    return ENTITY_TYPES.REF;
  } else if (styleAdapter.model instanceof StepEdgeModel) {
    return ENTITY_TYPES.EDGE;
  } else if (styleAdapter.model instanceof StepFaceModel) {
    return ENTITY_TYPES.FACE;
  } else {
    return ENTITY_TYPES.TEXT;
  }
};
